/**
 * LookupCombo — the XAF lookup control: a multi-COLUMN grid popup with a filter
 * box, used by any field whose value comes from a reference list.
 *
 * Two behaviours, one component:
 *   · single-select — click opens the grid popup; picking a row closes it and
 *     returns the WHOLE row (so the caller can fill dependent read-only fields,
 *     e.g. Client → Address + Phone, or Test → its TestMethod id).
 *   · CheckedComboBox (`multi`) — the popup is a checkbox list that STAYS OPEN
 *     while you tick, and the closed field shows the picks semicolon-joined
 *     ("Water; Solid"), exactly like the legacy XAF CheckedComboBox.
 *
 * Optionally renders the inline green "＋" quick-add — the host owns the dialog.
 *
 * PROMOTED to components/ on 2026-07-28: it began life inside Sample Registration
 * with a note to move it here "when a second form needs the same control", and
 * SDMS Result Entry (Test / QCBatchID combos) is that second form. The `sr-*`
 * class names are kept so Sample Registration's markup is unaffected; hosts that
 * need a different TRIGGER look override `.sr-combo-v` in their own scoped CSS
 * (see `.sdms-root .sr-combo-v`).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import './LookupCombo.css';
import { markUserEdit } from '../hooks/useUnsavedGuard';

/** Column captions + rows for the grid popup; column 0 is the VALUE.
 *  (Structurally identical to Sample Registration's `SrLookup`, so existing
 *  callers keep working without a cast.) */
export interface LookupData { cols: string[]; rows: string[][] }

const POPUP_WIDTH = 520;

export interface LookupComboProps {
  /** Column captions + rows; column 0 is the value. */
  lookup: LookupData;
  /** Single-select: the chosen value ('' = none).  Ignored when `multi`. */
  value?: string;
  /** CheckedComboBox: the ticked values. */
  values?: string[];
  multi?: boolean;
  /** Identity of a row for multi-select (default: column 0). Needed when column 0
   *  is not unique — e.g. a Test list where one test name has several methods. */
  rowKey?: (row: string[]) => string;
  disabled?: boolean;
  /** OPT-IN: the popup still OPENS and shows the list and what is ticked, but nothing
   *  can be picked, ticked or cleared. G5's Pre-Login "Test" lookup behaves this way
   *  once the COC has samples (row clicks cancelled, tick boxes disabled) — unlike
   *  `disabled`, which stops the popup opening at all. */
  selectionLocked?: boolean;
  placeholder?: string;
  /** Single-select pick — receives the FULL row so dependants can be filled. */
  onPick?: (row: string[]) => void;
  /** CheckedComboBox toggle — receives the next full selection. */
  onToggle?: (next: string[], toggled: string[]) => void;
  /** Renders the inline green ＋; called when it is clicked. */
  onNew?: () => void;
  /** OPT-IN: render the inline ＋ as a second, visible way to OPEN the grid picker
   *  (not a create quick-add) — the "+" toggles the popup instead of calling onNew.
   *  Used by a lookup whose "+" should just re-open its own selection grid. */
  newOpensPicker?: boolean;
  newTitle?: string;
  /** Blocked reason — clicking warns instead of opening (e.g. "pick a matrix"). */
  blockedReason?: string;
  onBlocked?: (reason: string) => void;
  /** Extra class on the trigger (e.g. the missing-required flash). */
  className?: string;
  id?: string;
  /** Show the single cross-column filter box above the grid. Default true; OG's
   *  SDMS ribbon combos have no top box, so they pass false. */
  showFilter?: boolean;
  /** Show an INLINE per-column filter row (an input under each column caption,
   *  each filtering only its own column, AND-combined) — DevExpress's "auto filter
   *  row". Opt-in; default off so existing combos are unchanged. Independent of
   *  `showFilter` (the SDMS combos use this instead of the top box). */
  columnFilters?: boolean;
  /** With `columnFilters`, column indices that get NO filter input (the caption
   *  still shows) — e.g. a numeric count column like the SDMS Test combo's `Sx`. */
  noFilterCols?: number[];
  /** Popup width in px (default 520) — widen it for grids with many columns. */
  popupWidth?: number;
  /** Size the popup to the TRIGGER — the list lines up with its own field instead
   *  of the fixed 520px (floored at 220px so a narrow field still gets a usable
   *  list). Opt-in, for single-column pickers; wins over `popupWidth`. */
  popupMatchTrigger?: boolean;
  /** Per-column widths (number = px, or any CSS length), positionally aligned to
   *  `lookup.cols`; omitted entries size automatically. Mirrors OG setting explicit
   *  widths (e.g. the SDMS Test combo's Method column at 350). */
  colWidths?: (number | string)[];
  /** Let cell text wrap onto a second line instead of being clipped to one line
   *  (OG's `ItemStyle.Wrap = True` on the SDMS Test combo). */
  wrapCells?: boolean;
  /** Extra class on the portaled popup (`.sr-pick`) so a host can scope popup-only
   *  overrides — the popup renders at document.body, outside the host's subtree. */
  popupClassName?: string;
  /** CheckedComboBox only: show "Select all" / "Clear" quick actions in the popup
   *  footer. "Select all" ticks every row currently matching the filter; "Clear"
   *  empties the selection. Opt-in so existing multi combos are unchanged. */
  multiActions?: boolean;
  /** CheckedComboBox only: render Sample Registration's "(Select All)" FIRST ROW
   *  in the list (ticks / unticks every row the filter currently shows). Opt-in,
   *  and independent of `multiActions` — a host picks the row OR the header
   *  checkbox, not both. */
  selectAllRow?: boolean;
  /** Mark this combo as the form's preferred first-focus target (the shared
   *  `focusFirstFormControl` honours `[data-autofocus]` before the DOM-order
   *  default). One per form — the first match wins. */
  autoFocus?: boolean;
  /** OPT-IN: the text the CLOSED combo shows, overriding the default (the value,
   *  or the ticked keys joined). For a multi-select whose keys are Oids — the
   *  closed combo must never show a raw id — e.g. the reagent Test lookup, which
   *  shows XAF's "TestName | MethodNumber" text. Unset = unchanged behaviour. */
  labelText?: string;
  /** OPT-IN: render an "N/A" row FIRST in the popup. It is a CLIENT-ONLY
   *  placeholder, never a value: it is highlighted when nothing is selected
   *  (so opening the list shows "no value"), and picking it CLEARS the field —
   *  `onClear` for single-select, `onToggle([])` for multi. "N/A" itself is
   *  never passed to `onPick`, so it can never be saved. Unset = unchanged. */
  naOption?: boolean;
  /** Single-select: called when the N/A row is picked (clear the value). */
  onClear?: () => void;
  /** OPT-IN: show the chosen value as PLAIN text, like a normal input, instead
   *  of the grey filled label chip (`.sr-combo-tag`). Unset = unchanged. */
  plainValue?: boolean;
  /** OPT-IN: the field's caption, when it sits in a sibling cell rather than a
   *  `<label for>`. The trigger is then named "{caption}, {value}" so a screen
   *  reader announces both. Unset = unchanged (the button's text is its name). */
  ariaLabel?: string;
  /** OPT-IN (single-select): render the XAF ASPxGridLookup **edit** button (✎)
   *  welded to the LEFT of the caret — it opens the picked record for editing. The
   *  host passes it only while a value is picked (G5 shows ✎ only then). Unset =
   *  unchanged. */
  onEdit?: () => void;
  /** Tooltip for the ✎ button. Default "Edit". */
  editTitle?: string;
}

/** The N/A placeholder row — compared by IDENTITY, so a real row whose text
 *  happens to be "N/A" is never mistaken for it. */
const NA_ROW: string[] = ['N/A'];

export function LookupCombo({
  lookup, value = '', values: valuesProp = [], multi = false, rowKey, disabled = false, selectionLocked = false,
  placeholder = 'N/A', onPick, onToggle, onNew, newOpensPicker = false, newTitle,
  blockedReason, onBlocked, className, id,
  showFilter = true, columnFilters = false, noFilterCols, popupWidth, popupMatchTrigger = false, colWidths, wrapCells = false, popupClassName, multiActions = false, selectAllRow = false,
  autoFocus = false, labelText, naOption = false, onClear, plainValue = false, ariaLabel, onEdit, editTitle,
}: LookupComboProps) {
  // Defensive: `values` is a string[] (multi-select). A caller that mistakenly
  // passes a bare string must NOT blank the whole page — `values.join`/`.map`
  // would throw and crash the React tree. Normalise to an array (keeping a
  // non-empty string as a single value) so a bad prop degrades gracefully.
  const vp = valuesProp as unknown;
  const values: string[] = Array.isArray(vp) ? (vp as string[]) : (typeof vp === 'string' && vp ? [vp] : []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Keyboard-cursor row index while the popup is open (‑1 = no cursor yet). */
  const [active, setActive] = useState(-1);
  /** Per-column filter text, positionally aligned to `lookup.cols` (columnFilters). */
  const [colQ, setColQ] = useState<string[]>([]);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    // Measured at open (and on resize), so it tracks the field as the form reflows.
    const width = popupMatchTrigger
      ? Math.max(220, Math.round(r.width))
      : (popupWidth ?? POPUP_WIDTH);
    // Clamp inside the viewport so the popup is never clipped (§18.6).
    setPos({
      top: Math.min(r.bottom + 4, Math.max(8, window.innerHeight - 340)),
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
      width,
    });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => place();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (showFilter) filterRef.current?.focus();
      else if (columnFilters) popRef.current?.querySelector<HTMLInputElement>('.sr-pick-fin')?.focus();
    }, 20);
    return () => clearTimeout(t);
  }, [open, showFilter, columnFilters]);

  // Reset the keyboard cursor whenever the popup opens or the filter changes, so
  // the first ↓ lands on the top (matching) row.
  useEffect(() => { setActive(-1); }, [open, query, colQ]);

  // Keep the cursor row scrolled into view as ↑/↓ move it.
  useEffect(() => {
    if (!open || active < 0) return;
    popRef.current?.querySelector('tr.sr-pick-active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = columnFilters ? colQ.map(s => (s ?? '').trim().toLowerCase()) : [];
    const anyCol = active.some(Boolean);
    if (!q && !anyCol) return lookup.rows;
    return lookup.rows.filter(r => {
      if (q && !r.join(' ').toLowerCase().includes(q)) return false;
      // Each column's own filter is a case-insensitive substring on THAT column; all AND together.
      for (let i = 0; i < active.length; i++)
        if (active[i] && !String(r[i] ?? '').toLowerCase().includes(active[i]!)) return false;
      return true;
    });
  }, [lookup.rows, query, colQ, columnFilters]);

  /** What the popup lists and the keyboard walks: the N/A placeholder first
   *  (opt-in, always shown — filtering never hides the way to clear), then rows. */
  const listRows = useMemo(() => (naOption ? [NA_ROW, ...rows] : rows), [naOption, rows]);
  const noneSelected = multi ? values.length === 0 : !value;

  const toggleTrigger = () => {
    if (disabled) return;
    if (blockedReason) { onBlocked?.(blockedReason); return; }
    setQuery('');
    setColQ([]);
    setOpen(o => !o);
  };

  const pick = (row: string[]) => {
    if (selectionLocked) return;   // view-only popup — see the prop
    if (row === NA_ROW) {
      // The placeholder CLEARS — it is never handed to onPick as a value.
      markUserEdit(triggerRef.current);
      if (multi) { onToggle?.([], values); return; }
      onClear?.();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (multi) {
      const v = rowKey ? rowKey(row) : (row[0] ?? '');
      const next = values.includes(v) ? values.filter(x => x !== v) : [...values, v];
      markUserEdit(triggerRef.current);   // unsaved-changes guard: the user changed this field
      onToggle?.(next, row);
      return;   // CheckedComboBox stays open
    }
    markUserEdit(triggerRef.current);
    onPick?.(row);
    setOpen(false);
    // Return focus to the field after picking so keyboard nav keeps working and,
    // when this combo sits in a dialog, closing the dialog restores focus to its
    // opener rather than dropping to <body> (user report 2026-09-21). Same as Esc.
    triggerRef.current?.focus();
  };

  // Keyboard list navigation from the filter box (mouse-free, ADR-0013, user
  // request 2026-09-21): ↓/↑ move a cursor row (Home/End jump), Enter picks it
  // (multi toggles + stays open, like a click), Esc closes and returns to the
  // trigger. Wired on the top filter AND the per-column filter inputs.
  // Once the user has TYPED a filter, the top match is what they are after — not the
  // pinned N/A row, which stays first in every filtered list. So the first ↓ lands on
  // the first matching row, and Enter with no cursor yet picks that top match. Without
  // this, "type → ↓ → Enter" picked N/A and CLEARED the field, and "type → Enter" did
  // nothing (Reagent Prep Prep Name / Vendor Stock, user report 2026-11-04). With no
  // filter typed, ↓ still starts on N/A.
  const filtering = query.trim() !== '' || colQ.some(s => (s ?? '').trim() !== '');
  const firstMatch = naOption && filtering && rows.length > 0 ? 1 : 0;
  const onListKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a < 0 ? Math.min(firstMatch, listRows.length - 1) : Math.min(listRows.length - 1, a + 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a < 0 ? listRows.length - 1 : Math.max(0, a - 1))); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(listRows.length ? 0 : -1); }
    else if (e.key === 'End') { e.preventDefault(); setActive(listRows.length - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const r = active >= 0 ? listRows[active] : (filtering ? rows[0] : undefined);
      if (r) pick(r);
    }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
  };

  // CheckedComboBox quick actions (opt-in via `multiActions`).
  const filteredVals = rows.map(r => r[0] ?? '').filter(Boolean);
  const allFilteredSelected  = filteredVals.length > 0 && filteredVals.every(v => values.includes(v));
  const someFilteredSelected = filteredVals.some(v => values.includes(v));
  // Header checkbox: tick every filtered row, or untick them all if already all on.
  const toggleAllFiltered = () => {
    if (selectionLocked) return;
    markUserEdit(triggerRef.current);
    if (allFilteredSelected) {
      const remove = new Set(filteredVals);
      onToggle?.(values.filter(v => !remove.has(v)), filteredVals);
    } else {
      const all = new Set(values);
      for (const v of filteredVals) all.add(v);
      onToggle?.(Array.from(all), filteredVals);
    }
  };
  const clearAll = () => {
    if (selectionLocked) return;
    markUserEdit(triggerRef.current);
    onToggle?.([], values);
  };

  const label = labelText ?? (multi ? values.join('; ') : value);

  return (
    <div className="sr-combo">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`sr-combo-v${onNew || newOpensPicker ? ' sr-combo-v-hasnew' : ''}${onEdit && !multi ? ' sr-combo-v-hasedit' : ''}${className ? ` ${className}` : ''}`}
        disabled={disabled}
        onClick={toggleTrigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': label ? `${ariaLabel}, ${label}` : ariaLabel } : undefined)}
        {...(autoFocus ? { 'data-autofocus': '' } : undefined)}
      >
        {label
          ? (multi || plainValue
            ? <span className="sr-combo-val">{label}</span>
            : <span className="sr-combo-tag">{label}</span>)
          : <span className="sr-combo-ph">{placeholder}</span>}
        {(onNew || newOpensPicker) && (
          <span
            className="sr-combo-new"
            role="button"
            tabIndex={-1}
            title={newTitle ?? 'New'}
            // newOpensPicker: the ＋ is a second, visible way to OPEN the grid picker
            // (it toggles the popup); otherwise it is the create quick-add (onNew).
            onClick={(e) => { e.stopPropagation(); if (newOpensPicker) toggleTrigger(); else onNew?.(); }}
          >＋</span>
        )}
        {/* XAF ASPxGridLookup edit (✎) — left of the caret; opens the picked
            record. Works in view mode too only if the host passes it then. */}
        {onEdit && !multi && (
          <span
            className="sr-combo-edit"
            role="button"
            tabIndex={-1}
            title={editTitle ?? 'Edit'}
            aria-label={editTitle ?? 'Edit'}
            onClick={(e) => { e.stopPropagation(); if (!disabled) onEdit(); }}
          ><i className="ti ti-pencil" aria-hidden="true" /></span>
        )}
        <span className="sr-combo-caret" aria-hidden="true" />
      </button>

      {open && pos && createPortal(
        <div
          className={`sr-pick${wrapCells ? ' sr-pick-wrapcells' : ''}${popupClassName ? ` ${popupClassName}` : ''}`}
          ref={popRef}
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          role="listbox"
        >
          {showFilter && (
            <div className="sr-pick-f">
              <input
                ref={filterRef}
                placeholder="Filter…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onListKey}
              />
            </div>
          )}
          <div className="sr-pick-wrap">
            <table className="sr-pick-table">
              {colWidths && (
                <colgroup>
                  {multi && <col style={{ width: 34 }} />}
                  {lookup.cols.map((c, i) => {
                    const w = colWidths[i];
                    return <col key={c} style={w === undefined ? undefined : { width: w }} />;
                  })}
                </colgroup>
              )}
              <thead>
                <tr>
                  {multi && (
                    <th className="sr-pick-cb">
                      {multiActions && (
                        <input
                          type="checkbox"
                          ref={el => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                          checked={allFilteredSelected}
                          onChange={toggleAllFiltered}
                          onClick={e => e.stopPropagation()}
                          disabled={selectionLocked}
                          aria-label="Select all"
                          title="Select all / deselect all"
                        />
                      )}
                    </th>
                  )}
                  {lookup.cols.map((c, ci) => (
                    <th key={c} className={columnFilters ? 'sr-pick-fcol' : undefined}>
                      {columnFilters ? (
                        <div className="sr-pick-hc">
                          <span className="sr-pick-hl">{c}</span>
                          {!noFilterCols?.includes(ci) && (
                            <input
                              className="sr-pick-fin"
                              value={colQ[ci] ?? ''}
                              onChange={e => {
                                const val = e.target.value;
                                setColQ(prev => { const n = prev.slice(); n[ci] = val; return n; });
                              }}
                              onClick={e => e.stopPropagation()}
                              onKeyDown={onListKey}
                              aria-label={`Filter ${c}`}
                            />
                          )}
                        </div>
                      ) : c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {multi && selectAllRow && rows.length > 0 && (
                  <tr key="__selall__"
                      className={`sr-pick-selall${allFilteredSelected ? ' on' : ''}`}
                      onClick={toggleAllFiltered}>
                    <td className="sr-pick-cb">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={el => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                        onChange={toggleAllFiltered}
                        onClick={e => e.stopPropagation()}
                        disabled={selectionLocked}
                        aria-label="Select All"
                      />
                    </td>
                    <td colSpan={lookup.cols.length}>(Select All)</td>
                  </tr>
                )}
                {naOption && (
                  <tr key="__na__" className={`sr-pick-na${noneSelected ? ' on' : ''}${active === 0 ? ' sr-pick-active' : ''}`}
                      onClick={() => pick(NA_ROW)}>
                    {multi && <td className="sr-pick-cb" />}
                    <td colSpan={lookup.cols.length}>N/A</td>
                  </tr>
                )}
                {rows.length === 0 ? (
                  <tr>
                    <td className="sr-pick-empty" colSpan={lookup.cols.length + (multi ? 1 : 0)}>
                      No matches.
                    </td>
                  </tr>
                ) : rows.map((r, idx) => {
                  // Keyboard cursor index runs over listRows (N/A first when on).
                  const i = naOption ? idx + 1 : idx;
                  const v = rowKey ? rowKey(r) : (r[0] ?? '');
                  const on = multi ? values.includes(v) : v === value;
                  return (
                    <tr key={`${v}-${i}`} className={`${on ? 'on' : ''}${i === active ? ' sr-pick-active' : ''}`} onClick={() => pick(r)}>
                      {multi && (
                        <td className="sr-pick-cb">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={selectionLocked}
                            onChange={() => pick(r)}
                            onClick={e => e.stopPropagation()}
                            aria-label={v}
                          />
                        </td>
                      )}
                      {r.map((c, ci) => <td key={ci}>{c}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sr-pick-g">
            {multi && multiActions && (
              <span className="sr-pick-acts">
                <button type="button" onClick={clearAll} disabled={selectionLocked}>Clear</button>
              </span>
            )}
            <span>
              {multi ? `${values.length} selected · ${rows.length} items` : `${rows.length} items`}
            </span>
            {onNew && (
              <button className="ca-btn" style={{ padding: '2px 9px', fontSize: 11 }}
                onClick={() => { setOpen(false); onNew(); }}>
                <span className="tb-i tb-i-green">＋</span> New
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
